/**
 * Harmonic Drift - Instrument Manager
 * Handles custom instrument persistence and retrieval
 */

import { DEFAULT_INSTRUMENTS } from '../config/index.js';

const STORAGE_KEY = 'harmonic_drift_instruments';

class InstrumentManager {
    constructor() {
        this.customInstruments = [];
        this.load();
    }

    /**
     * Load custom instruments from localStorage
     */
    load() {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                this.customInstruments = JSON.parse(stored);
            }
        } catch (e) {
            console.error('Failed to load custom instruments:', e);
            this.customInstruments = [];
        }
    }

    /**
     * Save custom instruments to localStorage
     */
    save() {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(this.customInstruments));
        } catch (e) {
            console.error('Failed to save custom instruments:', e);
        }
    }

    /**
     * Add a new custom instrument
     * @param {Object} instrument - Instrument definition
     */
    add(instrument) {
        this.customInstruments.push(instrument);
        this.save();
    }

    /**
     * Update an existing custom instrument
     * @param {string} id - Instrument ID
     * @param {Object} newData - Updated instrument data
     */
    update(id, newData) {
        const idx = this.customInstruments.findIndex(i => i.id === id);
        if (idx !== -1) {
            this.customInstruments[idx] = { ...this.customInstruments[idx], ...newData };
            this.save();
        }
    }

    /**
     * Remove a custom instrument
     * @param {string} id - Instrument ID
     */
    remove(id) {
        this.customInstruments = this.customInstruments.filter(i => i.id !== id);
        this.save();
    }

    /**
     * Get all custom instruments
     * @returns {Array} Custom instruments
     */
    getCustom() {
        return [...this.customInstruments];
    }

    /**
     * Get all default instruments
     * @returns {Array} Default instruments
     */
    getDefaults() {
        return [...DEFAULT_INSTRUMENTS];
    }

    /**
     * Get active instrument set based on palette mode
     * @param {string} mode - 'default', 'custom', or 'all'
     * @returns {Array} Instruments for the selected mode
     */
    getActiveSet(mode) {
        switch (mode) {
            case 'custom':
                return this.getCustom();
            case 'all':
                return [...DEFAULT_INSTRUMENTS, ...this.customInstruments];
            case 'default':
            default:
                return this.getDefaults();
        }
    }

    /**
     * Get all instruments (default + custom)
     * @returns {Array} All instruments
     */
    getAll() {
        return [...DEFAULT_INSTRUMENTS, ...this.customInstruments];
    }

    /**
     * Find an instrument by ID
     * @param {string} id - Instrument ID
     * @returns {Object|undefined} Instrument or undefined
     */
    findById(id) {
        return this.getAll().find(i => i.id === id);
    }

    /**
     * Check if an instrument is custom (not default)
     * @param {string} id - Instrument ID
     * @returns {boolean}
     */
    isCustom(id) {
        return !id.startsWith('def_');
    }
}

// Export singleton instance
export const instrumentManager = new InstrumentManager();
